import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, money, dateFR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Search, Ban, Eye, RotateCcw } from "lucide-react";
import Plate from "@/components/Plate";
import { toast } from "sonner";

export default function VentesAnnulees() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/ventes", { params: { cancelled: true } });
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const restore = async (v) => {
    if (!window.confirm(`Restaurer la vente ${v.plaque} (${v.marque} ${v.modele}) dans Ventes en cours ?`)) return;
    try {
      await api.put(`/ventes/${v.id}`, { cancelled: false, cancelled_reason: "" });
      toast.success("Vente restaurée");
      load();
    } catch {
      toast.error("Erreur lors de la restauration");
    }
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    const cleaned = t.replace(/[-\s]/g, "");
    return items.filter(
      (v) =>
        (v.plaque || "").toLowerCase().includes(cleaned) ||
        (v.client_nom || "").toLowerCase().includes(t) ||
        (v.marque || "").toLowerCase().includes(t) ||
        (v.modele || "").toLowerCase().includes(t),
    );
  }, [items, q]);

  return (
    <div className="space-y-6" data-testid="ventes-annulees-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-red-700 font-semibold flex items-center gap-2">
            <Ban size={12} /> Archives · Annulées
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Ventes annulées</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {filtered.length} vente(s) annulée(s). Ces véhicules sont exclus du Dashboard et des Ventes en cours.
          </p>
        </div>
        <div className="relative w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="ventes-annulees-search"
            type="search"
            name="ventes-annulees-search"
            autoComplete="off"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher…"
            className="pl-9 rounded-sm"
          />
        </div>
      </header>

      <Card className="rounded-sm border-border overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-red-50/40 text-left text-xs uppercase tracking-wider text-red-800">
            <tr>
              <th className="px-6 py-3">Actions</th>
              <th className="px-6 py-3">Date vente</th>
              <th className="px-6 py-3">Plaque</th>
              <th className="px-6 py-3">Véhicule</th>
              <th className="px-6 py-3">Client</th>
              <th className="px-6 py-3 text-right">Prix vente</th>
              <th className="px-6 py-3">Motif</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-6 py-10 text-center text-muted-foreground">Chargement…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-muted-foreground">Aucune vente annulée.</td></tr>
            )}
            {filtered.map((v) => (
              <tr key={v.id} className="border-t hover:bg-red-50/30">
                <td className="px-6 py-3 whitespace-nowrap">
                  <Link
                    to={`/ventes/${v.id}`}
                    data-testid={`vannulee-view-${v.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-klein/5 hover:bg-klein/10 text-klein border border-klein/20 rounded-sm font-semibold mr-1"
                  >
                    <Eye size={12} /> Détail
                  </Link>
                  <button
                    onClick={() => restore(v)}
                    data-testid={`vannulee-restore-${v.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-sm font-semibold"
                    title="Remettre dans Ventes en cours"
                  >
                    <RotateCcw size={12} /> Restaurer
                  </button>
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">{dateFR(v.date_vente)}</td>
                <td className="px-6 py-3"><Plate value={v.plaque} small /></td>
                <td className="px-6 py-3 font-medium">{v.marque} {v.modele} {v.annee ? `(${v.annee})` : ""}</td>
                <td className="px-6 py-3">{v.client_nom}</td>
                <td className="px-6 py-3 text-right font-mono text-muted-foreground">{money(v.prix_vente)}</td>
                <td className="px-6 py-3 text-xs">{v.cancelled_reason || <span className="text-muted-foreground italic">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
